import React, {useCallback, useEffect, useMemo, useState} from "react";
import {Modal, ModalContent, styled, Text} from "@reside/ui";

import {SimpleFacilityModel} from "../../utils/types/facility";

import {FacilitySelect} from "./FacilitySelect";
import {WorkflowList} from "./WorkflowList";
import {WorkflowOption} from "./types";
import {navigateAfterWorkflowSelect} from "./navigation";
import {useFacilityWorkflowsQuery} from "./hooks/useFacilityWorkflowsQuery";
import {
  getSingleFacility,
  resolveActiveFacilityId,
} from "./resolveActiveFacilityId";

type Props = Readonly<{
  facilities: ReadonlyArray<SimpleFacilityModel>;
  isOpen: boolean;
  onRequestClose: () => void;
}>;

export const StartWorkflowModal = ({
  facilities,
  isOpen,
  onRequestClose,
}: Props) => {
  const hasSingleFacility = facilities.length === 1;
  const singleFacility = getSingleFacility(facilities);
  const [selectedFacilityId, setSelectedFacilityId] = useState<string | null>(
    null,
  );

  useEffect(() => {
    if (!isOpen) {
      setSelectedFacilityId(null);
    }
  }, [isOpen]);

  const activeFacilityId = resolveActiveFacilityId(
    facilities,
    selectedFacilityId,
  );

  useFacilityWorkflowsQuery(activeFacilityId ?? undefined, {
    enabled: isOpen && Boolean(activeFacilityId),
  });

  const activeFacility = useMemo(
    () => facilities.find(facility => facility.id === activeFacilityId),
    [activeFacilityId, facilities],
  );

  const handleWorkflowSelect = useCallback(
    (workflow: WorkflowOption) => {
      onRequestClose();

      if (!activeFacilityId) {
        return;
      }

      navigateAfterWorkflowSelect({
        facilityId: activeFacilityId,
        workflow,
      });
    },
    [activeFacilityId, onRequestClose],
  );

  if (facilities.length === 0) {
    return null;
  }

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onRequestClose}
      closeButtonPosition="right"
    >
      {() => (
        <StyledModalContent data-test-id="startWorkflowModal">
          <FacilityHeader>
            <HeaderLabel>Start new workflow for:</HeaderLabel>
            {hasSingleFacility && singleFacility ? (
              <FacilityName>{singleFacility.name}</FacilityName>
            ) : (
              <FacilitySelect
                facilities={facilities}
                selectedFacilityId={selectedFacilityId}
                onSelect={setSelectedFacilityId}
              />
            )}
          </FacilityHeader>
          <Content>
            {activeFacility ? (
              <WorkflowList
                facilityId={activeFacility.id}
                onSelect={handleWorkflowSelect}
              />
            ) : (
              <WorkflowPlaceholder data-test-id="startWorkflowPlaceholder">
                Choose facility to display its workflows
              </WorkflowPlaceholder>
            )}
          </Content>
        </StyledModalContent>
      )}
    </Modal>
  );
};

const StyledModalContent = styled(ModalContent)`
  overflow: visible;
  color: ${({theme}) => theme.color.darkBlue100};
`;

const FacilityHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${({theme}) => theme.space[3]}px;
  margin-bottom: ${({theme}) => theme.space[4]}px;
`;

const HeaderLabel = styled.span`
  flex-shrink: 0;
  font-size: 18px;
  font-weight: 500;
  white-space: nowrap;
`;

const WorkflowPlaceholder = styled(Text)`
  margin: 0;
  font-size: 16px;
  color: ${({theme}) => theme.color.gray100};
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({theme}) => theme.space[6]}px;
  margin-top: ${({theme}) => theme.space[6]}px;
  min-height: 80vh;
`;

const FacilityName = styled.span`
  min-width: 0;
  font-size: 18px;
  font-weight: 600;
  color: ${({theme}) => theme.color.textPrimary};
`;
